import {Theme} from '@mui/material';
import {
   ptrnDeshabilitarCampoFn,
   ptrnOcultarCampoFn,
   ObjectStrKeys,
   PtrnAccion,
   PtrnAccionesAdicionales,
   PtrnAccionPametroAuxiliar,
   PtrnCampoBase,
   PtrnCampoEditable,
   PtrnCampoLectura,
   ptrnCondicionFn,
   PtrnDataInput,
   ptrnSoloLecturaCampo,
   PtrnState,
   PtrnStateValue,
   PtrnTiposCampo,
   ptrnValidarEstadoExFn,
   PtrnValor_o_Fn
} from '../patronTypes';
import {PTRN_TIPO_NO_VALUE} from '../constants';
import {CampoEditableErrorList} from '../components/CampoEditable/campoEditableTypes';
import {PtrnStateMaestroDetalle} from '../hooks/usePtrnMaestroDetalleEstado/useRdcrMstrDet';
import {PtrnAccionesProps} from '../patronProps';
import {ValidateState} from 'lib.common';
import {PtrnProcesarUtilsCfg} from './ptrnProcesarCampo/ptrnProcesarUtils';
import ptrnProcesarCampo from './ptrnProcesarCampo';

//#region PtrnCfgUtils

export class PtrnError extends Error {
   constructor(msg: string) {
      super(`Ptrn -> ${msg}`);
   }
}
export class PtrnCfgUtils {
   static extraerPrimitivasCfg<T extends PtrnCampoEditable | PtrnCampoLectura>(
      configuracion: T[]
   ): T[] {
      if (configuracion.length <= 0) {
         throw new PtrnError('No se proporciono ninguna configuraicon de campos');
      }

      let listaPrimitivas: T[] = [];

      for (const currConf of configuracion) {
         if (currConf.tipo === PtrnTiposCampo.boton) {
            listaPrimitivas.push(currConf);
            continue;
         }
         if (currConf.tipo === PtrnTiposCampo.grupo || currConf.tipo === PtrnTiposCampo.tab) {
            // @ts-ignore en la siguiente linea, currConf.campos siempre existira debido a la validacion del if contenedor
            const grupoLista = PtrnCfgUtils.extraerPrimitivasCfg<T>(currConf.campos);
            listaPrimitivas = [...listaPrimitivas, ...grupoLista];
            continue;
         }

         if (currConf.tipo === PtrnTiposCampo.tabgroup) {
            // @ts-ignore en la siguiente linea, currConf.tabList siempre existira debido a la validacion del if contenedor
            for (const currTabConf of currConf.tabList) {
               const grupoLista = PtrnCfgUtils.extraerPrimitivasCfg<T>(currTabConf.campos);
               listaPrimitivas = [...listaPrimitivas, ...grupoLista];
            }
            continue;
         }
         listaPrimitivas.push(currConf);
      }

      return listaPrimitivas;
   }
}
//#endregion
//#region PtrnEstilosGenerales
export class PtrnEstilosGenerales {
   static containerMainContent(maxHeightVhMain: number): {
      maxHeight: string;
      //TODO definir tamanio del formulario default
      //minHeight: string;
      overflowY: 'scroll' | 'auto' | 'hidden';
   } {
      return maxHeightVhMain
         ? {
              maxHeight: `${maxHeightVhMain}vh`,
              //minHeight: `${maxHeightVhMain}vh`,
              overflowY: 'scroll'
           }
         : {
              maxHeight: '75vh',
              //minHeight: `${maxHeightVhMain}vh`,
              overflowY: 'auto'
           };
   }
   static containerSimpleRoot(theme: Theme) {
      return {
         height: '100%'
      };
   }
   static containerSimpleMargin(theme: Theme) {
      return {
         marginTop: theme.spacing(1),
         marginBottom: theme.spacing(1)
      };
   }
}
//#endregion

//#region PtrnPropsUtil
export class PtrnPropsUtil {
   static getPropValue<T>(
      defaultValue: T,
      propValue?: PtrnValor_o_Fn<T>,
      auxParam?: PtrnAccionPametroAuxiliar
   ): T {
      if (!propValue) {
         return defaultValue;
      }
      return typeof propValue === 'function' ? propValue(auxParam) : (propValue as T);
   }
   static genIdCampoEditable(campoConfig: PtrnCampoEditable) {
      return `campo-editable-${campoConfig.tipo}-${campoConfig.name}`;
   }

   /**
    *
    * @param {boolean | ptrnCondicionFn} [condicion]
    * @param {any} [valorCampo]
    * @param {Object} [camposData]
    * @returns retorna el valor de `condicion` si es booleano o, en caso de ser funcion, el resultado de ejecutar `condicion` con el valor actual del campo y el valor de los campos actuales
    */
   static exeCondicionFn(
      condicion?: boolean | ptrnCondicionFn,
      valorCampo?: any,
      camposData?: PtrnState
   ) {
      return (
         //solo evaluar condicion si se ha proporcionado un valor
         condicion != undefined &&
         //si condicion es booleano respetar el valor proporcionado
         ((typeof condicion === 'boolean' && condicion) ||
            //si condicion es funcion, retornar el valor de ejecutar la funcion con los parametros proporcionados
            (typeof condicion === 'function' && condicion(valorCampo, camposData)))
      );
   }
   /**
    *
    * @param {boolean | ptrnOcultarCampoFn} [condicion]
    * @param {any} [valorCampo]
    * @param {Object} [camposData]
    * @returns __true__ si el el campo debe ser oculto dependiento de la condicion definida, el valor actual del campo y el valor de los campos actuales
    */
   static esCampoOculto(
      condicion?: boolean | ptrnOcultarCampoFn,
      valorCampo?: any,
      camposData?: PtrnState
   ) {
      return PtrnPropsUtil.exeCondicionFn(condicion, valorCampo, camposData);
   }

   /**
    *
    * @param {boolean | ptrnOcultarCampoFn} [condicion]
    * @param {any} [valorCampo]
    * @param {Object} [camposData]
    * @returns __true__ si el el campo debe ser deshabilitado dependiento de la condicion definida, el valor actual del campo y el valor de los campos actuales
    */
   static esCampoDehabilitado(
      condicion?: boolean | ptrnDeshabilitarCampoFn,
      valorCampo?: any,
      camposData?: PtrnState
   ) {
      return PtrnPropsUtil.exeCondicionFn(condicion, valorCampo, camposData);
   }

   /**
    *
    * @param {boolean | ptrnOcultarCampoFn} [condicion]
    * @param {any} [valorCampo]
    * @param {Object} [camposData]
    * @returns __true__ si el el campo debe ser deshabilitado dependiento de la condicion definida, el valor actual del campo y el valor de los campos actuales
    */
   static esCampoSoloLectura(
      condicion?: boolean | ptrnSoloLecturaCampo,
      valorCampo?: any,
      camposData?: PtrnState
   ) {
      return PtrnPropsUtil.exeCondicionFn(condicion, valorCampo, camposData);
   }
   static getGridBrkpntValido(
      bkp?: number,
      options: {default: number; reducir?: number} = {default: 12}
   ): number {
      if (!bkp || bkp < 0 || bkp > 12) {
         return options.default;
      }
      if (options.reducir) {
         return bkp - options.reducir;
      }
      return bkp;
   }
   static getAccionesAdicionalesArr(accionesAdicionales?: PtrnAccionesAdicionales): PtrnAccion[] {
      if (!accionesAdicionales) {
         return [];
      }
      const accionesAdicionalesArr = Object.values(accionesAdicionales).flat();
      return accionesAdicionalesArr.filter((curr) => Boolean(curr));
   }
   static getDisabledAcciones(
      acciones: PtrnAccionesProps,
      camposData?: PtrnState
   ): {
      disabledInsert: boolean;
      disabledUpdate: boolean;
      disabledDelete: boolean;
   } {
      return {
         disabledInsert: PtrnPropsUtil.exeCondicionFn(
            acciones.deshabilitarInsert,
            undefined,
            camposData
         ),
         disabledUpdate: PtrnPropsUtil.exeCondicionFn(
            acciones.deshabilitarUpdate,
            undefined,
            camposData
         ),
         disabledDelete: PtrnPropsUtil.exeCondicionFn(
            acciones.deshabilitarDelete,
            undefined,
            camposData
         )
      };
   }
}
//#endregion

//#region PtrnStateUtil
interface ErrorToSetI {
   name: string;
   errores: string[] | null;
}
export class PtrnStateUtil {
   static isPtrnState(obj: PtrnDataInput, retornoIndeterminado: boolean = true) {
      const valuesKeys = Object.keys(obj);
      if (valuesKeys.length === 0) {
         //Es imposible determinar si el objeto proporcionado es PtrnState
         return retornoIndeterminado;
      }
      if (valuesKeys.length > 0 && (obj[valuesKeys[0]] as PtrnStateValue).errores !== undefined) {
         return true;
      }
      return false;
   }

   static hasErrors(state: PtrnState | PtrnStateMaestroDetalle): boolean {
      for (const key in state) {
         const value = state[key];

         if ('errores' in value) {
            // verificar si la propiedad errores es un arreglo no vacio
            if (Array.isArray(value.errores) && value.errores.length > 0) {
               return true; // retornar inmediatamente si hay 1 error
            }
         } else {
            // si no continen la propiedad errores verificar recursivamente el estado anidado
            if (PtrnStateUtil.hasErrors(value as PtrnState | PtrnStateMaestroDetalle)) {
               return true; // retornar true si se encontro un error en el estado anidado
            }
         }
      }
      return false;
   }

   static generatePatronState(
      camposPrimitivasConfig: PtrnCampoBase[],
      withValue?: ObjectStrKeys
   ): PtrnState {
      const state: PtrnState = {};
      for (let inx = 0; inx < camposPrimitivasConfig.length; inx++) {
         const currCampo = camposPrimitivasConfig[inx];
         if (currCampo.tipo === PtrnTiposCampo.boton) {
            continue;
         }
         if (state[currCampo.name] === undefined) {
            //valores por defecto del estado
            state[currCampo.name] = {valor: null, errores: null, fueModificado: false};
         }
         //value
         if (currCampo.defaultValue !== undefined) {
            state[currCampo.name].valor = currCampo.defaultValue;
         }
         if (withValue && withValue[currCampo.name] !== undefined) {
            state[currCampo.name].valor = withValue[currCampo.name];
         }
      }
      return state;
   }

   static getStringFromPtrnInput(fieldName: string, data: PtrnDataInput) {
      if (PtrnStateUtil.isPtrnState(data, false)) {
         return (data as PtrnState)[fieldName].valor.toString();
      } else {
         return data[fieldName].toString();
      }
   }
   static getValor_Y_Error(
      inputValue: PtrnState,
      campoConfig: PtrnCampoBase,
      erroresInput?: CampoEditableErrorList
   ): {valor: PtrnState | PtrnStateValue; errores?: CampoEditableErrorList} {
      let valor = inputValue;
      let errores = erroresInput;
      if (!PTRN_TIPO_NO_VALUE.includes(campoConfig.tipo)) {
         valor = inputValue[campoConfig.name]?.valor;

         if (erroresInput) {
            const isErrorInputArray = Array.isArray(erroresInput);
            const errorInputList: string[] = undefined;
            if (isErrorInputArray) {
               errores = [...inputValue[campoConfig.name]?.errores, ...erroresInput];
               if (errores.length === 0) {
                  errores = undefined;
               }
            }
         } else {
            errores = inputValue[campoConfig.name]?.errores;
         }
      }
      return {
         valor,
         errores
      };
   }
   static validarEstado: ptrnValidarEstadoExFn = async ({
      state,
      globalState,
      cfgState,
      onProcesarValidarCampoResult,
      camposEspecificos,
      ignorarCampos
   }) => {
      //0. sanitizar los inputs q definen los estado a validar
      const existeIgnorar = ignorarCampos && ignorarCampos.length > 0;
      const existeEspecificos = camposEspecificos && camposEspecificos.length > 0;
      const camposValidar: string[] = Object.keys(cfgState).filter((curr) => {
         if (existeIgnorar && ignorarCampos.includes(curr)) {
            return false;
         }
         if (existeEspecificos) {
            return camposEspecificos.includes(curr);
         }
         return true;
      });

      //1. validar los campos especificados usando la configuracion definida por parametro `cfgState`
      let estadoValicacion: ValidateState = ValidateState.SUCCESS;
      for (const nombreCampo of camposValidar) {
         const currCfg = PtrnProcesarUtilsCfg.getValidCfg(cfgState[nombreCampo], {
            campoNombre: nombreCampo,
            origen: 'usePtrnEstadoSimple->validarEstado'
         });
         if (currCfg) {
            const value = state[nombreCampo].valor;
            const validacionCampo = ptrnProcesarCampo(value, currCfg, state, globalState, {
               campoNombre: nombreCampo,
               origen: 'usePtrnEstadoSimple->validarEstado',
               aplicarOnBlur: true
            });
            await onProcesarValidarCampoResult(currCfg.name, validacionCampo);
            estadoValicacion = PtrnProcesarUtilsCfg.getMaxGravedad(
               estadoValicacion,
               validacionCampo.gravedadMaxima
            );
         }
      }
      return estadoValicacion;
   };
}
//#endregion
